import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';
import BaseValidationModal, {
  commonStyles,
  ValidationRuleModalProps,
} from 'validation_rules/components/simple_ui/BaseValidationRuleModal';

import {SimpleRuleInput} from './types';
import useSimpleRules, {ValidationRuleConstantInput, SimpleRulesState} from './useSimpleRules';
import {getInputForSimpleRule} from './utils';

const TEXT_FIELD_NOT_EQUALS_TEMPLATE_ID = 'svdrltmpl_textfieldnotequals';
const TEXT_FIELD_CONTAINS_TEMPLATE_ID = 'svdrltmpl_textfieldcontains';
const TEXT_FIELD_NOT_CONTAINS_TEMPLATE_ID = 'svdrltmpl_textfieldnotcontains';

const TEXT_FIELD_NOT_EQUALS_INPUT_KEY = 'value2';
const TEXT_FIELD_CONTAINS_INPUT_KEY = 'value2';
const TEXT_FIELD_NOT_CONTAINS_INPUT_KEY = 'value2';

interface FieldErrors {
  disallowedValueError?: string;
  containsValueError?: string;
  notContainsValueError?: string;
}

interface TextFieldModalProps extends ValidationRuleModalProps {
  disallowedValue: string | null;
  containsValue: string | null;
  notContainsValue: string | null;
  fieldDefinitionLabel: JSX.Element;
  handleUpdateSimpleRule: (
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput
  ) => void;
  handleRemoveSimpleRule: (validationRuleTemplateId: string) => void;
}

function getFieldErrors(
  disallowedValue: string | null,
  containsValue: string | null,
  notContainsValue: string | null
): FieldErrors {
  const fieldErrors: FieldErrors = {};
  if (containsValue && notContainsValue && containsValue.includes(notContainsValue)) {
    const errorMessage = `The value for 'Doesn't contain' cannot be a part of the 'Contains' value.`;
    fieldErrors.containsValueError = errorMessage;
    fieldErrors.notContainsValueError = errorMessage;
  }
  return fieldErrors;
}

export default function TextFieldModal(props: TextFieldModalProps): JSX.Element {
  const fieldErrors: FieldErrors = getFieldErrors(
    props.disallowedValue,
    props.containsValue,
    props.notContainsValue
  );
  const hasErrors =
    Boolean(fieldErrors.disallowedValueError) ||
    Boolean(fieldErrors.containsValueError) ||
    Boolean(fieldErrors.notContainsValueError);

  function onChangeInputValue(
    validationRuleTemplateId: string,
    inputKey: string,
    input: SimpleRuleInput
  ): void {
    // Remove the rule if the input is empty
    if (input === '') {
      props.handleRemoveSimpleRule(validationRuleTemplateId);
    } else {
      props.handleUpdateSimpleRule(validationRuleTemplateId, {[inputKey]: input});
    }
  }

  return (
    <BaseValidationModal {...props} disabled={hasErrors}>
      <Row className={css(commonStyles.middleRow)}>
        <Col xs={12} sm={12} md={12} lg={12}>
          <TextField
            label="Cannot equal"
            value={props.disallowedValue ?? ''}
            onChange={(e: InputChangeEvent) => {
              onChangeInputValue(
                TEXT_FIELD_NOT_EQUALS_TEMPLATE_ID,
                TEXT_FIELD_NOT_EQUALS_INPUT_KEY,
                e.target.value
              );
            }}
            error={
              fieldErrors && fieldErrors.disallowedValueError
                ? {
                    message: fieldErrors.disallowedValueError,
                    style: InputErrorMessageStyle.ERROR,
                  }
                : undefined
            }
            {...getTestProps({component: 'TextFieldModal', element: 'disallowValueInput'})}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6} sm={6} md={6} lg={6} className={css(commonStyles.leftColumn)}>
          <TextField
            label="Contains"
            value={props.containsValue ?? ''}
            onChange={(e: InputChangeEvent) => {
              onChangeInputValue(
                TEXT_FIELD_CONTAINS_TEMPLATE_ID,
                TEXT_FIELD_CONTAINS_INPUT_KEY,
                e.target.value
              );
            }}
            error={
              fieldErrors && fieldErrors.containsValueError
                ? {
                    message: fieldErrors.containsValueError,
                    style: InputErrorMessageStyle.ERROR,
                  }
                : undefined
            }
            {...getTestProps({component: 'TextFieldModal', element: 'containsValueInput'})}
          />
        </Col>
        <Col xs={6} sm={6} md={6} lg={6} className={css(commonStyles.rightColumn)}>
          <TextField
            label="Doesn't contain"
            value={props.notContainsValue ?? ''}
            onChange={(e: InputChangeEvent) => {
              onChangeInputValue(
                TEXT_FIELD_NOT_CONTAINS_TEMPLATE_ID,
                TEXT_FIELD_NOT_CONTAINS_INPUT_KEY,
                e.target.value
              );
            }}
            error={
              fieldErrors && fieldErrors.notContainsValueError
                ? {
                    message: fieldErrors.notContainsValueError,
                    style: InputErrorMessageStyle.ERROR,
                  }
                : undefined
            }
            {...getTestProps({component: 'TextFieldModal', element: 'notContainsInput'})}
          />
        </Col>
      </Row>
    </BaseValidationModal>
  );
}

interface TextFieldModalContainerProps {
  schemaField: SchemaField;
  onRequestSubmit: (result: SimpleRulesState) => void;
  onRequestClose: () => void;
}

export function TextFieldModalContainer(props: TextFieldModalContainerProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(props.schemaField);
  const simpleRules = simpleRulesState.simpleRules;
  const legacySimpleRules = simpleRulesState.legacySimpleRules;

  const disallowedValue = getInputForSimpleRule(
    simpleRules,
    TEXT_FIELD_NOT_EQUALS_TEMPLATE_ID,
    TEXT_FIELD_NOT_EQUALS_INPUT_KEY
  );
  const containsValue = getInputForSimpleRule(
    simpleRules,
    TEXT_FIELD_CONTAINS_TEMPLATE_ID,
    TEXT_FIELD_CONTAINS_INPUT_KEY
  );
  const notContainsValue = getInputForSimpleRule(
    simpleRules,
    TEXT_FIELD_NOT_CONTAINS_TEMPLATE_ID,
    TEXT_FIELD_NOT_CONTAINS_INPUT_KEY
  );

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={<FieldDefinitionLabel field={props.schemaField} schemaFieldSelectors={[]} />}
      size={ChipSize.MINI}
    />
  );

  return (
    <TextFieldModal
      disallowedValue={disallowedValue as string | null}
      containsValue={containsValue as string | null}
      notContainsValue={notContainsValue as string | null}
      fieldDefinitionLabel={fieldDefinitionLabel}
      handleUpdateSimpleRule={simpleRulesActions.handleUpdateSimpleRule}
      handleRemoveSimpleRule={simpleRulesActions.handleRemoveSimpleRule}
      onRequestSubmit={() => {
        props.onRequestSubmit(simpleRulesState);
      }}
      onRequestClose={props.onRequestClose}
      legacySimpleRules={legacySimpleRules}
      handleUpdateLegacyRules={simpleRulesActions.handleUpdateLegacySimpleRules}
    />
  );
}

const styles = StyleSheet.create({
  fieldDefinitionLabel: {
    marginRight: 0,
  },
});
